import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface SuggestedKeyword {
  keyword: string;
  volume: number;
  cpc: string;
  currency?: string; // Optional for backward compatibility
  competition: number;
}

interface SEOKeywordRendererProps {
  data: {
    top_competitors: string[] | null;
    suggested_keywords?: SuggestedKeyword[];
    seo_title?: string;
    meta_description?: string;
    response_text?: string;
  };
}

const SEOKeywordRenderer: React.FC<SEOKeywordRendererProps> = ({ data }) => {
  if (!data.suggested_keywords && !data.top_competitors && !data.seo_title) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="text-sm text-gray-500">No SEO keyword data available</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="font-semibold text-gray-900">🔍 SEO Keyword Research</div>

      {/* SEO Title */}
      {data.seo_title && (
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-700">SEO Title:</div>
          <div className="text-sm text-gray-900 font-medium bg-white border border-gray-200 rounded p-2">
            {data.seo_title}
          </div>
        </div>
      )}

      {/* Meta Description */}
      {data.meta_description && (
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-700">Meta Description:</div>
          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded p-2">
            {data.meta_description}
          </div>
        </div>
      )}

      {/* Suggested Keywords Table */}
      {data.suggested_keywords && data.suggested_keywords.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">
            Suggested Keywords ({data.suggested_keywords.length}):
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th className="text-left p-2 font-medium text-gray-700">Keyword</th>
                  <th className="text-right p-2 font-medium text-gray-700">Volume</th>
                  <th className="text-right p-2 font-medium text-gray-700">CPC</th>
                  <th className="text-right p-2 font-medium text-gray-700">Competition</th>
                </tr>
              </thead>
              <tbody>
                {data.suggested_keywords.map((keyword, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-2 text-gray-900 font-medium">{keyword.keyword}</td>
                    <td className="p-2 text-right text-gray-600">
                      {keyword.volume.toLocaleString()}
                    </td>
                    <td className="p-2 text-right text-gray-600">{keyword.currency || '$'}{keyword.cpc}</td>
                    <td className="p-2 text-right">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          keyword.competition < 0.3
                            ? 'bg-green-100 text-green-800'
                            : keyword.competition < 0.7
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {(keyword.competition * 100).toFixed(0)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Competitors */}
      {data.top_competitors && data.top_competitors.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">
            Top Competitors ({data.top_competitors.length}):
          </div>
          <div className="bg-white border border-gray-200 rounded p-3">
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
              {data.top_competitors.map((competitor, index) => (
                <li key={index} className="text-blue-600 hover:underline">
                  <span rel="noopener noreferrer" className="ml-1">
                    {competitor}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {/* Response Text (if available) */}
      {data.response_text && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">Analysis:</div>
          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded p-3 prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-strong:text-gray-900 prose-ul:text-gray-600 prose-ol:text-gray-600 prose-table:border-gray-300 prose-th:bg-gray-50 prose-th:text-gray-900 prose-td:text-gray-700">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.response_text}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEOKeywordRenderer;
